import React, { useEffect, useMemo, useState } from 'react';
import {
    Tooltip,
    Badge,
    Box,
    Button,
    CircularProgress,
    IconButton,
    List,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField as MuiTextField,
    Typography,
} from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import { Add, DeleteOutline, EditOutlined, ErrorOutline, FolderCopy } from '@mui/icons-material';
import { DialogV2, Autocomplete } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { useBoolean } from 'react-hanger';
import { Tab } from '@mui/material';
import type {
    CampaignMediaGroupParameter,
    CampaignMediaGroupParameterName,
    CampaignMediaGroupParameterSearchType,
} from 'c-sdk';

type GroupParameterDraft = {
    parameter: CampaignMediaGroupParameterName;
    searchType: CampaignMediaGroupParameterSearchType;
    identifiers: string[];
};

type Props = {
    mediaGroups: Record<string, number>;
    groupIdsByName: Record<string, number>;
    activeGroup: string | null;
    setActiveGroup: React.Dispatch<React.SetStateAction<string | null>>;
    totalMediaCount: number;
    ungroupedMediaCount: number;
    handleCreateGroup: (
        group_name: string,
        groupParameterConfigs?: GroupParameterDraft[],
    ) => void | Promise<void>;
    handleDeleteGroup: (groupId: number, groupName: string) => void | Promise<void>;
    handleDeleteGroupParameter: (groupParameterId: number) => void | Promise<void>;
    handleUpdateGroupParameters: (
        groupName: string,
        groupParameters: CampaignMediaGroupParameter[],
        configs: GroupParameterDraft[],
    ) => void | Promise<void>;
    campaignMediaGroupParametersByGroupName?: Record<string, CampaignMediaGroupParameter[]>;
    groupWarningsByName?: Record<string, string[]>;
    groupActionLoading?: boolean;
    selectedMediaIds?: string[];
    onAttachSelectionToGroup?: (groupName: string) => void | Promise<void>;
};

const parameterTabs: Array<{
    label: string;
    value: CampaignMediaGroupParameterName;
    translation: 'id' | 'name' | 'creativeRef';
}> = [
    { label: 'ID', value: 'id', translation: 'id' },
    { label: 'Media Name', value: 'name', translation: 'name' },
    { label: 'Media Ref', value: 'creative_ref', translation: 'creativeRef' },
];

// Users almost always group by media name; ID is only the default when criteria come from a row selection.
const defaultParameterTab: CampaignMediaGroupParameterName = 'name';

const normalizeDrafts = (drafts: GroupParameterDraft[]) => {
    const seen = new Set<string>();

    return drafts.reduce<GroupParameterDraft[]>((acc, draft) => {
        const identifiers = Array.from(
            new Set(draft.identifiers.map(value => value.trim()).filter(Boolean)),
        );

        if (identifiers.length === 0) {
            return acc;
        }

        const normalizedDraft = {
            parameter: draft.parameter,
            searchType: draft.searchType,
            identifiers,
        };
        const key = JSON.stringify(normalizedDraft);

        if (seen.has(key)) {
            return acc;
        }

        seen.add(key);
        acc.push(normalizedDraft);
        return acc;
    }, []);
};

const normalizeExistingParameterToDraft = (
    groupParameter: CampaignMediaGroupParameter,
): GroupParameterDraft => ({
    parameter: groupParameter.parameter,
    searchType: groupParameter.search_type,
    identifiers:
        groupParameter.search_type === 'contains_all'
            ? groupParameter.identifier
                  .split('%')
                  .map(value => value.trim())
                  .filter(Boolean)
            : [groupParameter.identifier],
});

const isSameDraft = (left: GroupParameterDraft, right: GroupParameterDraft) =>
    left.parameter === right.parameter &&
    left.searchType === right.searchType &&
    left.identifiers.length === right.identifiers.length &&
    left.identifiers.every((identifier, index) => identifier === right.identifiers[index]);

const MediaTableGroups: React.FC<Props> = ({
    mediaGroups,
    groupIdsByName,
    activeGroup,
    setActiveGroup,
    totalMediaCount,
    ungroupedMediaCount,
    handleCreateGroup,
    handleDeleteGroup,
    handleDeleteGroupParameter,
    handleUpdateGroupParameters,
    campaignMediaGroupParametersByGroupName = {},
    groupWarningsByName = {},
    groupActionLoading = false,
    selectedMediaIds = [],
    onAttachSelectionToGroup,
}) => {
    const t = useCommonTranslation();
    const mediaGroupKeys = Object.keys(mediaGroups);
    const newGroup = useBoolean(false);
    const [groupToDelete, setGroupToDelete] = useState<{ id: number; name: string } | null>(null);
    const [groupCriteriaToEdit, setGroupCriteriaToEdit] = useState<{
        groupName: string;
        parameters: CampaignMediaGroupParameter[];
    } | null>(null);
    const [isDeletingGroup, setIsDeletingGroup] = useState(false);
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [isSavingGroupParameters, setIsSavingGroupParameters] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [activeCreateTab, setActiveCreateTab] =
        useState<CampaignMediaGroupParameterName>(defaultParameterTab);
    const [activeEditTab, setActiveEditTab] =
        useState<CampaignMediaGroupParameterName>(defaultParameterTab);
    const [newGroupSearchType, setNewGroupSearchType] =
        useState<CampaignMediaGroupParameterSearchType>('contains');
    const [editSearchType, setEditSearchType] =
        useState<CampaignMediaGroupParameterSearchType>('contains');
    const [newGroupIdentifierInput, setNewGroupIdentifierInput] = useState('');
    const [editIdentifierInput, setEditIdentifierInput] = useState('');
    const [newGroupCriteria, setNewGroupCriteria] = useState<GroupParameterDraft[]>([]);
    const [editCriteria, setEditCriteria] = useState<GroupParameterDraft[]>([]);

    const searchTypeOptions = useMemo(
        () => [
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.contains'),
                value: 'contains',
            },
            {
                label: t(
                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.containsAll',
                ),
                value: 'contains_all',
            },
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.exact'),
                value: 'exact',
            },
            {
                label: t(
                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.startsWith',
                ),
                value: 'starts_with',
            },
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.endsWith'),
                value: 'ends_with',
            },
        ],
        [t],
    );

    const searchTypeLabelMap = useMemo(
        () =>
            searchTypeOptions.reduce<Record<string, string>>((acc, option) => {
                acc[option.value] = option.label;
                return acc;
            }, {}),
        [searchTypeOptions],
    );

    const getSearchTypeLabel = (searchType: CampaignMediaGroupParameterSearchType) =>
        searchTypeLabelMap[searchType] ?? searchType;

    const resetCreateForm = () => {
        setNewGroupName('');
        setActiveCreateTab(defaultParameterTab);
        setNewGroupSearchType('contains');
        setNewGroupIdentifierInput('');
        setNewGroupCriteria([]);
    };

    const openCreateNewGroup = () => {
        resetCreateForm();
        if (selectedMediaIds.length > 0) {
            setActiveCreateTab('id');
            setNewGroupCriteria(
                normalizeDrafts([
                    {
                        parameter: 'id',
                        searchType: 'exact',
                        identifiers: selectedMediaIds,
                    },
                ]),
            );
        }
        newGroup.setTrue();
    };

    useEffect(() => {
        if (!groupCriteriaToEdit) {
            return;
        }

        setEditCriteria(
            normalizeDrafts(groupCriteriaToEdit.parameters.map(normalizeExistingParameterToDraft)),
        );
        setActiveEditTab(defaultParameterTab);
        setEditSearchType('contains');
        setEditIdentifierInput('');
    }, [groupCriteriaToEdit]);

    useEffect(() => {
        if (!newGroup.value) {
            setIsCreatingGroup(false);
        }
    }, [newGroup.value]);

    useEffect(() => {
        if (groupToDelete === null) {
            setIsDeletingGroup(false);
        }
    }, [groupToDelete]);

    const addDraftRow = (
        currentDrafts: GroupParameterDraft[],
        parameter: CampaignMediaGroupParameterName,
        searchType: CampaignMediaGroupParameterSearchType,
        identifiers: string[],
    ) =>
        normalizeDrafts([
            ...currentDrafts,
            {
                parameter,
                searchType,
                identifiers,
            },
        ]);

    const renderCriteriaTables = (
        criteria: GroupParameterDraft[],
        savedParameters: CampaignMediaGroupParameter[] | undefined,
        onDeleteDraft: (draft: GroupParameterDraft) => void,
        canDeleteSaved: boolean,
        activeTab: CampaignMediaGroupParameterName,
    ) =>
        parameterTabs
            .filter(tab => tab.value === activeTab)
            .map(tab => {
                const filteredCriteria = criteria.filter(draft => draft.parameter === tab.value);

                if (filteredCriteria.length === 0) {
                    return null;
                }

                return (
                    <Box key={tab.value}>
                        <Typography variant="body2" gutterBottom>
                            {tab.label}
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {t(
                                            'Modules.Main.Campaigns.CampaignSchedule.mediaTable.paramsEditDialog.searchType',
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            'Modules.Main.Campaigns.CampaignSchedule.mediaTable.paramsEditDialog.identifier',
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t(
                                            'Modules.Main.Campaigns.CampaignSchedule.mediaTable.paramsEditDialog.actions',
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCriteria.map(draft => {
                                    const matchingSavedParameter = savedParameters?.find(
                                        parameter =>
                                            isSameDraft(
                                                normalizeExistingParameterToDraft(parameter),
                                                draft,
                                            ),
                                    );

                                    return (
                                        <TableRow
                                            key={`${draft.parameter}-${
                                                draft.searchType
                                            }-${draft.identifiers.join('|')}`}
                                        >
                                            <TableCell>
                                                {getSearchTypeLabel(draft.searchType)}
                                            </TableCell>
                                            <TableCell>{draft.identifiers.join(', ')}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    disabled={groupActionLoading}
                                                    onClick={() => {
                                                        if (
                                                            matchingSavedParameter &&
                                                            canDeleteSaved
                                                        ) {
                                                            handleDeleteGroupParameter(
                                                                matchingSavedParameter.id,
                                                            );
                                                            setGroupCriteriaToEdit(prev =>
                                                                prev
                                                                    ? {
                                                                          ...prev,
                                                                          parameters:
                                                                              prev.parameters.filter(
                                                                                  p =>
                                                                                      p.id !==
                                                                                      matchingSavedParameter.id,
                                                                              ),
                                                                      }
                                                                    : prev,
                                                            );
                                                        }
                                                        onDeleteDraft(draft);
                                                    }}
                                                >
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                );
            })
            .filter(Boolean);

    const renderComposer = ({
        activeTab,
        searchType,
        setSearchType,
        identifierInput,
        setIdentifierInput,
        onTabChange,
        onAdd,
        disabled,
        draftCriteria,
        savedParameters,
    }: {
        activeTab: CampaignMediaGroupParameterName;
        searchType: CampaignMediaGroupParameterSearchType;
        setSearchType: React.Dispatch<React.SetStateAction<CampaignMediaGroupParameterSearchType>>;
        identifierInput: string;
        setIdentifierInput: React.Dispatch<React.SetStateAction<string>>;
        onTabChange: (value: CampaignMediaGroupParameterName) => void;
        onAdd: (identifiersToUse: string[]) => void;
        disabled: boolean;
        draftCriteria: GroupParameterDraft[];
        savedParameters?: CampaignMediaGroupParameter[];
    }) => (
        <Stack gap={2}>
            <TabContext value={activeTab}>
                <TabList
                    onChange={(_, value) => {
                        if (value) {
                            onTabChange(value as CampaignMediaGroupParameterName);
                        }
                    }}
                >
                    {parameterTabs.map(tab => {
                        const unsavedDraftCount = draftCriteria.filter(
                            draft =>
                                draft.parameter === tab.value &&
                                !(savedParameters ?? []).some(parameter =>
                                    isSameDraft(
                                        normalizeExistingParameterToDraft(parameter),
                                        draft,
                                    ),
                                ),
                        ).length;
                        const savedCount = (savedParameters ?? []).filter(
                            parameter => parameter.parameter === tab.value,
                        ).length;
                        const tabLabel =
                            savedCount > 0 ? `${tab.label} (${savedCount})` : tab.label;
                        return (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                sx={{ pr: 3 }}
                                label={
                                    <Badge
                                        badgeContent={unsavedDraftCount}
                                        color="error"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                top: -2,
                                                right: -4,
                                                height: 14,
                                                minWidth: 14,
                                                padding: '0 4px',
                                                fontSize: 10,
                                            },
                                        }}
                                    >
                                        <span>{tabLabel}</span>
                                    </Badge>
                                }
                            />
                        );
                    })}
                </TabList>
            </TabContext>
            <Stack direction="row" gap={1} alignItems="flex-start">
                <Box minWidth={220}>
                    <Autocomplete
                        textFieldProps={{
                            label: t(
                                'Modules.Main.Campaigns.CampaignSchedule.mediaTable.paramsEditDialog.searchType',
                            ),
                            size: 'small',
                        }}
                        options={searchTypeOptions}
                        value={searchType}
                        onChange={value =>
                            setSearchType(value as CampaignMediaGroupParameterSearchType)
                        }
                    />
                </Box>
                <Box flex={1}>
                    <MuiTextField
                        multiline
                        minRows={4}
                        fullWidth
                        size="small"
                        label={t(
                            'Modules.Main.Campaigns.CampaignSchedule.mediaTable.paramsEditDialog.identifier',
                        )}
                        placeholder="One identifier per line"
                        value={identifierInput}
                        onChange={event => setIdentifierInput(event.target.value)}
                    />
                </Box>
                <Button
                    variant="contained"
                    disabled={
                        disabled ||
                        identifierInput
                            .split('\n')
                            .map(value => value.trim())
                            .filter(Boolean).length === 0
                    }
                    onClick={() =>
                        onAdd(
                            identifierInput
                                .split('\n')
                                .map(value => value.trim())
                                .filter(Boolean),
                        )
                    }
                >
                    {t('CommonWords.Add')}
                </Button>
            </Stack>
        </Stack>
    );

    const ungroupedLabel = t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.ungrouped');

    return (
        <Box display="flex" flex={1} width="100%" flexDirection="column">
            <Box pb={4} display="flex" justifyContent="space-between">
                <Button variant="outlined" size="small" onClick={openCreateNewGroup}>
                    {t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.createNewGroup')}
                </Button>
            </Box>

            <DialogV2
                onClose={() => {
                    newGroup.setFalse();
                    resetCreateForm();
                }}
                open={newGroup.value}
                maxWidth="xl"
                title={t(
                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.createNewGroup',
                )}
            >
                {groupActionLoading || isCreatingGroup ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Stack gap={2} pt={2}>
                        <MuiTextField
                            label={t(
                                'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.newGroupName',
                            )}
                            value={newGroupName}
                            onChange={event => setNewGroupName(event.target.value)}
                            fullWidth
                            size="small"
                        />
                        {renderComposer({
                            activeTab: activeCreateTab,
                            searchType: newGroupSearchType,
                            setSearchType: setNewGroupSearchType,
                            identifierInput: newGroupIdentifierInput,
                            setIdentifierInput: setNewGroupIdentifierInput,
                            onTabChange: setActiveCreateTab,
                            disabled: groupActionLoading,
                            draftCriteria: newGroupCriteria,
                            onAdd: identifiersToUse => {
                                setNewGroupCriteria(prev =>
                                    addDraftRow(
                                        prev,
                                        activeCreateTab,
                                        newGroupSearchType,
                                        identifiersToUse,
                                    ),
                                );
                                setNewGroupIdentifierInput('');
                            },
                        })}
                        {newGroupCriteria.length > 0 && (
                            <Box>
                                <Typography variant="body2" gutterBottom>
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.criteria',
                                    )}
                                </Typography>
                                {renderCriteriaTables(
                                    newGroupCriteria,
                                    undefined,
                                    draft =>
                                        setNewGroupCriteria(prev =>
                                            prev.filter(item => !isSameDraft(item, draft)),
                                        ),
                                    false,
                                    activeCreateTab,
                                )}
                            </Box>
                        )}
                        <Stack direction="row" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                disabled={!newGroupName.trim() || groupActionLoading}
                                onClick={async () => {
                                    setIsCreatingGroup(true);
                                    await handleCreateGroup(newGroupName.trim(), newGroupCriteria);
                                    newGroup.setFalse();
                                    resetCreateForm();
                                }}
                            >
                                {t(
                                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.createNewGroup',
                                )}
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </DialogV2>

            <DialogV2
                onClose={() => setGroupCriteriaToEdit(null)}
                open={groupCriteriaToEdit !== null}
                maxWidth="xl"
                title={t(
                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.editCriteriaTitle',
                )}
            >
                {groupActionLoading || isSavingGroupParameters ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Stack gap={2}>
                        {renderComposer({
                            activeTab: activeEditTab,
                            searchType: editSearchType,
                            setSearchType: setEditSearchType,
                            identifierInput: editIdentifierInput,
                            setIdentifierInput: setEditIdentifierInput,
                            onTabChange: setActiveEditTab,
                            disabled: groupActionLoading,
                            draftCriteria: editCriteria,
                            savedParameters: groupCriteriaToEdit?.parameters,
                            onAdd: identifiersToUse => {
                                setEditCriteria(prev =>
                                    addDraftRow(
                                        prev,
                                        activeEditTab,
                                        editSearchType,
                                        identifiersToUse,
                                    ),
                                );
                                setEditIdentifierInput('');
                            },
                        })}
                        {editCriteria.length > 0 && (
                            <Box>
                                <Typography variant="body2" gutterBottom>
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.criteria',
                                    )}
                                </Typography>
                                {renderCriteriaTables(
                                    editCriteria,
                                    groupCriteriaToEdit?.parameters,
                                    draft =>
                                        setEditCriteria(prev =>
                                            prev.filter(item => !isSameDraft(item, draft)),
                                        ),
                                    true,
                                    activeEditTab,
                                )}
                            </Box>
                        )}
                        <Stack direction="row" justifyContent="flex-end" gap={1}>
                            <Button variant="outlined" onClick={() => setGroupCriteriaToEdit(null)}>
                                {t('CommonWords.Cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                disabled={groupActionLoading}
                                onClick={async () => {
                                    if (!groupCriteriaToEdit) {
                                        return;
                                    }

                                    setIsSavingGroupParameters(true);
                                    await handleUpdateGroupParameters(
                                        groupCriteriaToEdit.groupName,
                                        groupCriteriaToEdit.parameters,
                                        editCriteria,
                                    );
                                    setIsSavingGroupParameters(false);
                                    setGroupCriteriaToEdit(null);
                                }}
                            >
                                {t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.apply')}
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </DialogV2>

            <DialogV2
                onClose={() => setGroupToDelete(null)}
                open={groupToDelete !== null}
                maxWidth="sm"
                title={t(
                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.deleteGroupTitle',
                )}
            >
                <Stack gap={2}>
                    {groupActionLoading || isDeletingGroup ? (
                        <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Typography variant="body1">
                            {t(
                                'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.deleteGroupMessage',
                                {
                                    name: groupToDelete?.name,
                                },
                            )}
                        </Typography>
                    )}
                    <Stack direction="row" justifyContent="flex-end" gap={1}>
                        <Button
                            variant="outlined"
                            onClick={() => setGroupToDelete(null)}
                            disabled={groupActionLoading || isDeletingGroup}
                        >
                            {t('CommonWords.Cancel')}
                        </Button>
                        <Button
                            color="error"
                            variant="contained"
                            disabled={groupActionLoading || isDeletingGroup}
                            onClick={async () => {
                                if (!groupToDelete) {
                                    return;
                                }

                                setIsDeletingGroup(true);
                                await handleDeleteGroup(groupToDelete.id, groupToDelete.name);
                                setGroupToDelete(null);
                            }}
                        >
                            {t(
                                'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.deleteGroupConfirm',
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </DialogV2>

            <Box flex={1} overflow="auto">
                <List disablePadding>
                    <Stack gap={1}>
                        <Box
                            key="all"
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={1}
                            borderRadius={1.5}
                            bgcolor={activeGroup === 'All' ? 'action.selected' : 'background.paper'}
                            sx={{
                                cursor: 'pointer',
                                border: '2px solid transparent',
                                borderBottom: '2px solid',
                                borderBottomColor:
                                    activeGroup === 'All' ? 'primary.main' : 'grey.300',
                            }}
                            onClick={() => setActiveGroup('All')}
                        >
                            <Stack direction="row" gap={1}>
                                <FolderCopy fontSize="small" style={{ marginRight: 8 }} />
                                <Typography variant="body2" flex={1}>
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.mediaTable.groups.all',
                                    )}{' '}
                                    ({totalMediaCount})
                                </Typography>
                            </Stack>
                        </Box>
                        <Box
                            key="ungrouped"
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={1}
                            borderRadius={1.5}
                            bgcolor={activeGroup === null ? 'action.selected' : 'background.paper'}
                            sx={{
                                cursor: 'pointer',
                                border: '2px solid transparent',
                                borderBottom: '2px solid',
                                borderBottomColor:
                                    activeGroup === null ? 'primary.main' : 'grey.300',
                            }}
                            onClick={() => setActiveGroup(null)}
                        >
                            <Stack direction="row" gap={1}>
                                <FolderCopy fontSize="small" style={{ marginRight: 8 }} />
                                <Typography variant="body2" flex={1}>
                                    {ungroupedLabel} ({ungroupedMediaCount})
                                </Typography>
                            </Stack>
                        </Box>
                        {mediaGroupKeys.map(groupKey => {
                            const isActive = activeGroup === groupKey;
                            const warnings = groupWarningsByName[groupKey] ?? [];

                            return (
                                <Box
                                    key={groupKey}
                                    display="flex"
                                    flexDirection="column"
                                    p={1}
                                    borderRadius={1.5}
                                    bgcolor={isActive ? 'action.selected' : 'background.paper'}
                                    sx={{
                                        cursor: 'pointer',
                                        border: '2px solid transparent',
                                        borderBottom: '2px solid',
                                        borderBottomColor: isActive ? 'primary.main' : 'grey.300',
                                    }}
                                    onClick={() => setActiveGroup(groupKey)}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        width="100%"
                                    >
                                        <Stack direction="row" gap={1}>
                                            <FolderCopy
                                                fontSize="small"
                                                style={{ marginRight: 8 }}
                                            />
                                            <Typography variant="body2" flex={1}>
                                                {groupKey} ({mediaGroups[groupKey]})
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row">
                                            {warnings.length > 0 && (
                                                <Tooltip title={warnings.join('\n')}>
                                                    <IconButton
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                        }}
                                                    >
                                                        <ErrorOutline
                                                            fontSize="small"
                                                            sx={{
                                                                color: isActive
                                                                    ? 'common.white'
                                                                    : 'error.main',
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <IconButton
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    setGroupCriteriaToEdit({
                                                        groupName: groupKey,
                                                        parameters:
                                                            campaignMediaGroupParametersByGroupName[
                                                                groupKey
                                                            ] ?? [],
                                                    });
                                                }}
                                            >
                                                <EditOutlined />
                                            </IconButton>
                                            <Tooltip
                                                title={
                                                    selectedMediaIds.length === 0
                                                        ? 'Select media rows first'
                                                        : `Attach ${selectedMediaIds.length} selected to group`
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        disabled={selectedMediaIds.length === 0}
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            onAttachSelectionToGroup?.(groupKey);
                                                        }}
                                                    >
                                                        <Add />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <IconButton
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    setGroupToDelete({
                                                        id: groupIdsByName[groupKey],
                                                        name: groupKey,
                                                    });
                                                }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </List>
            </Box>
        </Box>
    );
};

export default MediaTableGroups;
